/**
 * Pipeline History Component
 * Displays historical pipeline executions with filtering capabilities
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  TextField,
  Grid,
  Alert,
  Collapse,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import CancelIcon from '@mui/icons-material/Cancel';
import { getPipelineHistory } from '../services/datalakeGrpcService.ts';

interface PipelineHistoryItem {
  pipelineId: string;
  creator: string;
  creatorId: string;
  purpose: string;
  modelName: string;
  status: string;
  overallProgress: number;
  startTime: string;
  endTime: string;
  durationSeconds: number;
  batchId: string;
  imageCount: number;
  errorMessage: string;
}

const PipelineHistory: React.FC = () => {
  const [historyItems, setHistoryItems] = useState<PipelineHistoryItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedRow, setExpandedRow] = useState<string | null>(null);

  // Filters
  const [creatorFilter, setCreatorFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [limitFilter, setLimitFilter] = useState(50);

  const loadHistory = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await getPipelineHistory(
        creatorFilter || undefined,
        undefined, // creatorId - could be used for filtering by Keycloak user ID
        undefined, // startDate
        undefined, // endDate
        limitFilter
      );
      setHistoryItems(response.pipelinesList || []);
    } catch (err: any) {
      console.error('Error loading pipeline history:', err);
      setError(err.message || 'Failed to load pipeline history');
    } finally {
      setLoading(false);
    }
  };

  const formatDuration = (seconds: number): string => {
    if (!seconds) return 'N/A';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);

    if (hours > 0) {
      return `${hours}h ${minutes}m ${secs}s`;
    } else if (minutes > 0) {
      return `${minutes}m ${secs}s`;
    } else {
      return `${secs}s`;
    }
  };

  const formatDateTime = (isoString: string): string => {
    if (!isoString) return 'N/A';
    try {
      const date = new Date(isoString);
      return date.toLocaleString();
    } catch {
      return 'Invalid date';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircleIcon color="success" />;
      case 'failed':
        return <ErrorIcon color="error" />;
      case 'cancelled':
        return <CancelIcon color="warning" />;
      default:
        return null;
    }
  };

  const getStatusColor = (status: string): "success" | "error" | "warning" | "default" => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'failed':
        return 'error';
      case 'cancelled':
        return 'warning';
      default:
        return 'default';
    }
  };

  const handleToggleExpand = (pipelineId: string) => {
    setExpandedRow(expandedRow === pipelineId ? null : pipelineId);
  };

  useEffect(() => {
    loadHistory();
  }, [creatorFilter, statusFilter, limitFilter]);

  // Filter by status on client side
  const filteredItems = statusFilter === 'all'
    ? historyItems
    : historyItems.filter(item => item.status === statusFilter);

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h5">Pipeline History</Typography>
        <Tooltip title="Refresh">
          <IconButton onClick={loadHistory} disabled={loading}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Filters */}
      <Card variant="outlined" sx={{ mb: 2 }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Creator"
                variant="outlined"
                size="small"
                value={creatorFilter}
                onChange={(e) => setCreatorFilter(e.target.value)}
                placeholder="Filter by creator"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  label="Status"
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="completed">Completed</MenuItem>
                  <MenuItem value="failed">Failed</MenuItem>
                  <MenuItem value="cancelled">Cancelled</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Limit</InputLabel>
                <Select
                  value={limitFilter}
                  label="Limit"
                  onChange={(e) => setLimitFilter(e.target.value as number)}
                >
                  <MenuItem value={25}>25</MenuItem>
                  <MenuItem value={50}>50</MenuItem>
                  <MenuItem value={100}>100</MenuItem>
                  <MenuItem value={200}>200</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading && historyItems.length === 0 && (
        <Box display="flex" justifyContent="center" p={3}>
          <Typography>Loading pipeline history...</Typography>
        </Box>
      )}

      {!loading && filteredItems.length === 0 && (
        <Alert severity="info">
          No pipeline history found.
        </Alert>
      )}

      {filteredItems.length > 0 && (
        <TableContainer component={Paper} variant="outlined">
          <Table>
            <TableHead>
              <TableRow>
                <TableCell width={50}></TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Purpose</TableCell>
                <TableCell>Creator</TableCell>
                <TableCell>Model</TableCell>
                <TableCell align="right">Images</TableCell>
                <TableCell>Duration</TableCell>
                <TableCell>End Time</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredItems.map((item) => (
                <React.Fragment key={item.pipelineId}>
                  <TableRow hover>
                    <TableCell>
                      <IconButton
                        size="small"
                        onClick={() => handleToggleExpand(item.pipelineId)}
                      >
                        {expandedRow === item.pipelineId ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      </IconButton>
                    </TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        {getStatusIcon(item.status)}
                        <Chip
                          label={item.status}
                          size="small"
                          color={getStatusColor(item.status)}
                        />
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                        {item.purpose}
                      </Typography>
                    </TableCell>
                    <TableCell>{item.creator}</TableCell>
                    <TableCell>
                      <Typography variant="body2" noWrap sx={{ maxWidth: 150 }}>
                        {item.modelName}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">{item.imageCount || 'N/A'}</TableCell>
                    <TableCell>{formatDuration(item.durationSeconds)}</TableCell>
                    <TableCell>{formatDateTime(item.endTime)}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell colSpan={8} sx={{ p: 0, border: 0 }}>
                      <Collapse in={expandedRow === item.pipelineId} timeout="auto" unmountOnExit>
                        <Box sx={{ p: 2, bgcolor: 'background.default' }}>
                          <Grid container spacing={2}>
                            <Grid item xs={12} md={6}>
                              <Typography variant="caption" color="text.secondary">
                                Pipeline ID:
                              </Typography>
                              <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                                {item.pipelineId}
                              </Typography>
                            </Grid>
                            {item.batchId && (
                              <Grid item xs={12} md={6}>
                                <Typography variant="caption" color="text.secondary">
                                  Batch ID:
                                </Typography>
                                <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                                  {item.batchId}
                                </Typography>
                              </Grid>
                            )}
                            <Grid item xs={12} md={6}>
                              <Typography variant="caption" color="text.secondary">
                                Start Time:
                              </Typography>
                              <Typography variant="body2">
                                {formatDateTime(item.startTime)}
                              </Typography>
                            </Grid>
                            <Grid item xs={12} md={6}>
                              <Typography variant="caption" color="text.secondary">
                                Progress:
                              </Typography>
                              <Typography variant="body2">
                                {Math.round(item.overallProgress)}%
                              </Typography>
                            </Grid>
                            {item.errorMessage && (
                              <Grid item xs={12}>
                                <Typography variant="caption" color="text.secondary">
                                  Error Message:
                                </Typography>
                                <Typography variant="body2" color="error">
                                  {item.errorMessage}
                                </Typography>
                              </Grid>
                            )}
                          </Grid>
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default PipelineHistory;
